import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { Header } from '@Components/header/header'
import { ProgressStepper } from '@Components/progress-stepper/progress-stepper'
import { LoadingIndicator } from '@Components/loading-indicator/loading-indicator'
import { PlayerDataServiceAPI } from '@API/data-services/player-data-service'
import { RootState } from '@AppState/redux-store'
import { useClaimFormSteps } from './use-claim-form-steps'
import { ClaimFormSchema } from './types'
import { ClaimFormGeoDataResponse } from '@interaction-gaming/player-data-service-browser-client'
import { GenericSchemaStep } from './screens/generic-schema-step/generic-schema-step'
import { ReviewStep } from './screens/review-step/review-step'
import { ClaimFormConfirmation } from './confirmation/claim-form-confirmation'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config/types'

import './claim-form-flow.scss'

const CLASS_NAME = 'claim-form-flow'

export const ClaimFormFlow = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const claimReservationId = searchParams.get('claim-id')

  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: schema, isLoading: schemaLoading } = useQuery({
    queryKey: ['claimFormSchema'],
    queryFn: () => PlayerDataServiceAPI.getClaimFormSchema(),
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
  })

  const { data: geoData, isLoading: geoDataLoading } = useQuery({
    queryKey: ['claimFormGeoData'],
    queryFn: () => PlayerDataServiceAPI.getClaimFormGeoData(),
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
  })

  // Fetch existing claim form if reservation has one (edit mode)
  const { data: existingForm, isLoading: existingFormLoading } = useQuery({
    queryKey: ['claimForm', claimReservationId],
    queryFn: () => PlayerDataServiceAPI.checkClaimFormExists(claimReservationId!),
    enabled: !!claimReservationId,
    retry: false,
  })

  // Read reservation from Redux (already loaded by the booking flow), fall back to API
  const reduxReservation = useSelector(
    (state: RootState) => state.claimReservation.claimReservation,
  )
  const { data: fetchedReservation } = useQuery({
    queryKey: ['claimReservation', claimReservationId],
    queryFn: () => PlayerDataServiceAPI.getAppointmentById(claimReservationId!),
    enabled: !!claimReservationId && !reduxReservation && !existingForm && !existingFormLoading,
    retry: false,
  })
  const reservation = reduxReservation ?? fetchedReservation

  // Pre-fill from reservation details (name, phone, email, ticket count)
  const reservationPrefill = useMemo(() => {
    if (!reservation) {
      return null
    }
    const details = reservation.claimReservationDetails
    const nameParts = (details.name || '').split(' ')
    const numberOfTickets = details.numberOfTickets || 1
    return {
      claimant: {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
      },
      contact: {
        mobilePhoneNumber: details.phone || '',
        emailAddress: details.email || '',
      },
      tickets: Array.from({ length: numberOfTickets }, () => ({
        prizeAmount: '',
        gameType: '',
        barcodeNumber: '',
      })),
    }
  }, [reservation])

  // Merge prefill with user-entered data — user data takes priority per key
  const effectiveFormData = useMemo(() => {
    let base: Record<string, unknown> = {}
    if (reservationPrefill) {
      base = { ...reservationPrefill }
    }
    return { ...base, ...formData }
  }, [reservationPrefill, formData])

  const steps = useClaimFormSteps(schema as ClaimFormSchema | undefined)

  const submitMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (claimReservationId) {
        return PlayerDataServiceAPI.submitClaimFormForReservation(claimReservationId, data)
      }
      return PlayerDataServiceAPI.submitClaimForm(data)
    },
    onSuccess: () => {
      setIsSubmitted(true)
    },
    onError: () => {
      setSubmitError('Something went wrong. Please try again.')
    },
  })

  const handleStepSubmit = useCallback((stepKeys: string[], data: Record<string, unknown>) => {
    setFormData((prev) => {
      const updated = { ...prev }
      for (const key of stepKeys) {
        updated[key] = data[key]
      }
      return updated
    })
    setCurrentStep((prev) => prev + 1)
  }, [])

  const handleBack = useCallback(() => {
    if (currentStep === 0) {
      navigate(-1)
      return
    }
    setCurrentStep((prev) => prev - 1)
  }, [currentStep, navigate])

  const handleSubmit = useCallback(() => {
    setSubmitError(null)
    submitMutation.mutate(effectiveFormData)
  }, [effectiveFormData, submitMutation])

  const handleEditStep = useCallback((stepIndex: number) => {
    setCurrentStep(stepIndex)
  }, [])

  if (isSubmitted) {
    return <ClaimFormConfirmation />
  }

  // Wait for existing form check before rendering (defaultValue only applies on mount)
  const prefillLoading = claimReservationId && existingFormLoading

  if (schemaLoading || geoDataLoading || prefillLoading) {
    return (
      <>
        <Header title="Ticket Receipt Form" />
        <section className={`${CLASS_NAME}__loading`}>
          <LoadingIndicator active={true} />
        </section>
      </>
    )
  }

  if (existingForm) {
    return (
      <>
        <Header title="Ticket Receipt Form" />
        <section className={`${CLASS_NAME}__received`}>
          <h2>Form already submitted</h2>
          <p>
            A claim form has already been submitted for this appointment. Please contact your claim
            center if you need to make changes.
          </p>
        </section>
      </>
    )
  }

  if (!schema || !geoData || steps.length === 0) {
    return null
  }

  const activeStep = steps[currentStep]
  const progressSteps = steps.map((s) => ({ label: s.label }))

  const renderContents = () => {
    if (!activeStep) {
      return null
    }

    // Review step (always last)
    if (activeStep.key === 'review') {
      return (
        <ReviewStep
          formData={effectiveFormData}
          steps={steps}
          onSubmit={handleSubmit}
          onBack={handleBack}
          onEditStep={handleEditStep}
          isSubmitting={submitMutation.isPending}
          error={submitError}
        />
      )
    }

    const StepComponent = activeStep.component ?? GenericSchemaStep

    // Build step-specific schema slice
    const fullSchema = schema as ClaimFormSchema
    const stepSchema: ClaimFormSchema = {
      type: 'object',
      properties: {},
      required: [],
      barcodeFormats: fullSchema.barcodeFormats,
    }
    for (const key of activeStep.schemaKeys) {
      if (fullSchema.properties[key]) {
        stepSchema.properties[key] = fullSchema.properties[key]
      }
    }

    return (
      <StepComponent
        schema={stepSchema}
        geoData={geoData as ClaimFormGeoDataResponse}
        formData={effectiveFormData}
        claimReservationId={claimReservationId}
        onSubmit={(data) => handleStepSubmit(activeStep.schemaKeys, data)}
        onBack={handleBack}
      />
    )
  }

  return (
    <>
      <Header title="Ticket Receipt Form" />
      <section className={CLASS_NAME}>
        <ProgressStepper steps={progressSteps} currentStepIndex={currentStep} />
        <section className={`${CLASS_NAME}__contents`}>{renderContents()}</section>
      </section>
    </>
  )
}
